import { useState, useEffect, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, CheckCircle2, Mail } from "lucide-react";

const STORAGE_KEY = "eliada_newsletter_subscribed";
const DELAY_MS = 1500;
const TIME_TRIGGER_MS = 3 * 60 * 1000; // 3 minutes on site

export function NewsletterPopup() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const shownRef = useRef(false);

  // Only suppress permanently for users who have already subscribed
  const hasSubscribed = () => {
    try { return localStorage.getItem(STORAGE_KEY) === "true"; }
    catch { return false; }
  };

  const dismiss = useCallback((reason: "close" | "subscribed" = "close") => {
    setVisible(false);
    // Only persist subscription — dismissed users see it again on next visit
    if (reason === "subscribed") {
      try { localStorage.setItem(STORAGE_KEY, "true"); } catch {}
    }
  }, []);

  const show = useCallback(() => {
    if (shownRef.current || hasSubscribed()) return;
    shownRef.current = true;
    setTimeout(() => setVisible(true), DELAY_MS);
  }, []);

  useEffect(() => {
    if (hasSubscribed()) return;

    // Trigger 1: exit intent — mouse leaves the top of viewport
    const onMouseLeave = (e: MouseEvent) => {
      if (e.clientY < 20) show();
    };

    // Trigger 2: scroll depth — 40% scroll depth reached
    const onScroll = () => {
      const scrolled = window.scrollY;
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0 && scrolled / totalHeight > 0.4) show();
    };

    // Trigger 3: time on site — show after 3 minutes regardless of scroll
    const timer = setTimeout(() => show(), TIME_TRIGGER_MS);

    document.addEventListener("mouseleave", onMouseLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    };
  }, [show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot || !email) return;
    setStatus("loading");
    try {
      const emailClean = email.trim().toLowerCase();

      const response = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: emailClean,
          name: name.trim(),
          honeypot,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Something went wrong. Please try again.");
      }

      setStatus("success");
      setTimeout(() => dismiss("subscribed"), 2800);
    } catch (err: any) {
      setStatus("error");
      setErrorMsg(err.message || "Something went wrong. Please try again.");
      setTimeout(() => { setStatus("idle"); setErrorMsg(""); }, 4000);
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Overlay */}
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm"
            onClick={() => dismiss()}
          />

          {/* Popup */}
          <motion.div
            key="popup"
            initial={{ opacity: 0, scale: 0.92, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 16 }}
            transition={{ type: "spring", stiffness: 340, damping: 30 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center px-4 pointer-events-none"
          >
            <div className="relative w-full max-w-md pointer-events-auto overflow-hidden rounded-2xl shadow-2xl">
              {/* Gold header band */}
              <div className="bg-primary relative overflow-hidden px-8 py-7">
                {/* Diagonal pattern */}
                <div className="absolute inset-0 opacity-10"
                  style={{
                    backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 16px, rgba(0,0,0,0.06) 16px, rgba(0,0,0,0.06) 17px)`,
                  }}
                />
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
                <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-black/10 blur-2xl" />

                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-black/20 flex items-center justify-center">
                      <Mail size={15} className="text-black" />
                    </div>
                    <span className="text-black/60 font-bold uppercase tracking-widest text-xs">Newsletter</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-display font-black text-black leading-tight uppercase tracking-tight">
                    Newsletter Signup
                  </h2>
                  <p className="mt-2 text-black/70 text-sm leading-relaxed">
                    Exclusive athlete signings, sports insights & agency updates — straight to your inbox.
                  </p>
                </div>

                {/* Close button */}
                <button
                  onClick={() => dismiss()}
                  aria-label="Close popup"
                  className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-black/10 hover:bg-black/20 text-black/60 hover:text-black transition-colors z-20"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Body */}
              <div className="bg-white px-8 py-7 min-h-[200px]">
                {status === "success" ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col items-center text-center py-4 gap-3"
                    data-testid="popup-success"
                  >
                    <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center">
                      <CheckCircle2 size={28} className="text-black" />
                    </div>
                    <h3 className="text-xl font-display font-black text-gray-900 uppercase">You're In!</h3>
                    <p className="text-gray-500 text-sm max-w-xs">
                      Welcome to the Eliada family. Watch your inbox for exclusive updates.
                    </p>
                  </motion.div>
                ) : (
                    <form
                      onSubmit={handleSubmit}
                      className="space-y-3"
                      data-testid="popup-form"
                    >
                      {/* Honeypot */}
                      <input
                        type="text"
                        name="website_url"
                        value={honeypot}
                        onChange={e => setHoneypot(e.target.value)}
                        className="absolute -left-[9999px] w-0 h-0 overflow-hidden"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                      />

                      <div>
                        <input
                          type="text"
                          value={name}
                          onChange={e => setName(e.target.value)}
                          placeholder="Your name (optional)"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/60 text-sm transition-all"
                        />
                      </div>

                      <div>
                        <input
                          type="email"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          placeholder="Enter your email address"
                          required
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/60 text-sm transition-all"
                        />
                      </div>

                      {status === "error" && (
                        <motion.p
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-600 text-xs font-medium px-1"
                        >
                          {errorMsg}
                        </motion.p>
                      )}

                      <button
                        type="submit"
                        disabled={status === "loading"}
                        className="w-full flex items-center justify-center gap-2 bg-black text-white font-display font-bold uppercase tracking-wider text-sm py-3.5 rounded-xl hover:bg-gray-900 active:scale-[0.98] transition-all disabled:opacity-60"
                      >
                        <Send size={14} />
                        {status === "loading" ? "Subscribing…" : "Join Now"}
                      </button>

                      <p className="text-center text-xs text-gray-400 pt-1">
                        No spam, ever. Unsubscribe anytime.
                      </p>
                    </form>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
